/**
 * Il client verso l'orchestratore.
 *
 * Una sola responsabilità: parlare HTTP e restituire risposte già validate con
 * gli stessi schemi Zod che il backend usa per accettarle.
 *
 * Tre esiti che la schermata deve saper distinguere, e che qui diventano tre
 * tipi diversi invece di tre `if` sparsi:
 *
 * - **200 con `ok:false`** — il dominio dice di no e spiega perché. Non è un
 *   errore: è la risposta che l'operatore deve leggere.
 * - **403 `causa: 'conferma'`** — il gesto scrive un nome e serve ridigitare la
 *   password. Chi chiama apre la richiesta e **ritenta il gesto**.
 * - **401** — la sessione non c'è più: si torna all'accesso.
 */
import { zEsitoComando } from '@errebi/shared';
/**
 * Cosa fare quando una richiesta scopre che la sessione non c'è più.
 *
 * Serve perché il 401 non arriva solo dai gesti: arriva anche dalle **letture**,
 * che non passano da `esegui` e finiscono in react-query. Senza questo gancio
 * una sessione scaduta mostrava un messaggio d'errore dentro la schermata,
 * lasciando l'operatore su una postazione che non funziona più — e con un
 * token invalido salvato che nessuno toglieva.
 */
let alloScadere = () => { };
export function quandoScadeLaSessione(f) {
    alloScadere = f;
}
/** Lo usa anche il client del flusso, che non passa da `chiama`. */
export function segnalaScadenza() {
    alloScadere();
}
export class SessioneScaduta extends Error {
    constructor() {
        super('Sessione assente o scaduta.');
        this.name = 'SessioneScaduta';
    }
}
export class ConfermaRichiesta extends Error {
    gesto;
    constructor(gesto, message) {
        super(message);
        this.gesto = gesto;
        this.name = 'ConfermaRichiesta';
    }
}
export class ErroreApi extends Error {
    stato;
    constructor(message, stato) {
        super(message);
        this.stato = stato;
        this.name = 'ErroreApi';
    }
}
/**
 * Una richiesta, e i tre modi di dire di no. **Il solo posto** in cui questa
 * lettura è scritta.
 *
 * Prima ce n'erano due, uguali riga per riga: questa e `chiamaFlusso` in
 * `flusso.ts`, che parla con le rotte di reparto. Due copie di un `if` sul 403
 * vogliono dire che il giorno che se ne corregge una l'altra resta indietro —
 * e la seconda è quella che apre la richiesta della password, cioè la cosa che
 * si nota solo in reparto.
 *
 * Prende l'URL già intero perché è l'unica differenza fra i due chiamanti.
 */
export async function richiesta(url, token, opzioni = {}) {
    const conCorpo = opzioni.corpo !== undefined;
    /*
     * Il multipart si riconosce **dal tipo del corpo**, non da un'opzione in più:
     * chi manda un file non deve ricordarsi di dichiararlo, o il giorno che se lo
     * dimentica il corpo finisce dentro `JSON.stringify` e arriva come `{}`.
     *
     * Serve alla foto scattata al banco (regola 38-bis), che è l'unica cosa che
     * l'API riceve in multipart. Sta **dentro questa funzione** e non in una
     * seconda: qui vivono il 401 che fa scadere la sessione e il 403 che apre la
     * richiesta della password, e una `fetch` a mano se li perderebbe entrambi.
     *
     * Il `typeof` difende da un ambiente senza `FormData` — jsdom vecchi, un
     * render sul server — dove `instanceof` esploderebbe su ogni richiesta.
     */
    const multipart = typeof FormData !== 'undefined' && opzioni.corpo instanceof FormData;
    const risposta = await fetch(url, {
        method: opzioni.metodo ?? (conCorpo ? 'POST' : 'GET'),
        headers: {
            // Il content-type di un multipart **lo scrive il browser**, col boundary
            // dentro: impostarlo a mano manda il corpo al parser sbagliato, e la
            // risposta è un 400 senza niente che spieghi perché.
            ...(conCorpo && !multipart ? { 'content-type': 'application/json' } : {}),
            ...(token ? { authorization: `Bearer ${token}` } : {}),
        },
        // Solo se c'è: `body: undefined` e «nessun corpo» sono due cose diverse
        // per `RequestInit` quando i tipi facoltativi sono presi alla lettera.
        ...(conCorpo
            ? { body: multipart ? opzioni.corpo : JSON.stringify(opzioni.corpo) }
            : {}),
    });
    const dati = await risposta.json().catch(() => null);
    if (risposta.status === 401) {
        alloScadere();
        throw new SessioneScaduta();
    }
    if (risposta.status === 403) {
        const corpo = (dati ?? {});
        if (corpo.causa === 'conferma') {
            throw new ConfermaRichiesta(corpo.gesto ?? '', corpo.motivo ?? 'Conferma la tua identità.');
        }
        throw new ErroreApi(corpo.motivo ?? 'Non permesso.', 403);
    }
    if (!risposta.ok) {
        const corpo = (dati ?? {});
        throw new ErroreApi(corpo.motivo ?? risposta.statusText, risposta.status);
    }
    return dati;
}
export async function chiama(percorso, token, opzioni = {}) {
    return richiesta(`/api${percorso}`, token, opzioni);
}
/**
 * Una lettura **validata**: la risposta passa dallo schema di `@errebi/shared`
 * prima di entrare nella schermata.
 *
 * È la differenza fra `as Promise<Utente[]>` e sapere davvero cosa è arrivato.
 * Il cast dice al compilatore di fidarsi e basta: se il backend smette di
 * mandare un campo, il tipo continua a prometterlo e la schermata disegna
 * `undefined` in mezzo a una tabella — un guasto che non ha uno stack e che si
 * scopre da uno screenshot. Con lo schema, la risposta sbagliata si ferma qui e
 * dice quale campo, con lo stesso messaggio che il backend userebbe per
 * rifiutare una richiesta.
 */
export async function leggi(percorso, token, schema, opzioni = {}) {
    return schema.parse(await chiama(percorso, token, opzioni));
}
export async function comando(percorso, token, corpo) {
    return zEsitoComando.parse(await chiama(percorso, token, { metodo: 'POST', corpo }));
}
/**
 * **The paths stay Italian, and it is a choice** — the same one already made for
 * the DTO fields (see CLAUDE.md, §Convenzioni → Lingua).
 *
 * A route is a wire contract, and nothing type-checks it: this string and
 * `@Controller('auth')` in the API are two halves that no compiler ever brings
 * together. Translating one half is a silent break — it happened on
 * 2026-08-26 (`/auth/sign-in` against `/api/auth/accesso`) and it took down the
 * bench login on master. The tests missed it because a mocked `fetch` asserts
 * this very string against itself: the mismatch lives exactly where no test
 * looks.
 *
 * So: when the API renames a route, this changes with it, in the same commit.
 * Never the other way round.
 */
export async function accedi(login, password) {
    const r = (await chiama('/auth/accesso', null, {
        metodo: 'POST',
        corpo: { login, password },
    }));
    return { ...r, via: 'password' };
}
export async function confermaIdentita(token, password) {
    const r = (await chiama('/auth/conferma', token, {
        metodo: 'POST',
        corpo: { password },
    }));
    return { ...r, via: 'password' };
}
export async function configurazioneAccesso() {
    return (await chiama('/auth/configurazione', null));
}
/** `credenziale` is what Google's button hands to the browser. */
export async function accediConGoogle(credenziale) {
    const r = (await chiama('/auth/google', null, {
        metodo: 'POST',
        corpo: { credential: credenziale },
    }));
    return { ...r, via: 'google' };
}
export async function confermaIdentitaConGoogle(token, credenziale) {
    const r = (await chiama('/auth/conferma-google', token, {
        metodo: 'POST',
        corpo: { credential: credenziale },
    }));
    return { ...r, via: 'google' };
}
//# sourceMappingURL=transport.js.map